import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-location';
import { apiRawFetch } from '../client';

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!email) {
            setError('Email is required');
            return;
        }

        setIsLoading(true);

        try {
            const response = await apiRawFetch('api/auth/forgot-password', 'POST', {
                email,
            });

            if (response.ok) {
                setIsSubmitted(true);
            } else {
                setError('Failed to send password reset email. Please try again.');
            }
        } catch (err) {
            setError(`An error occurred. Please try again. ${err}`);
        } finally {
            setIsLoading(false);
        }
    };

    if (isSubmitted) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Card>
                    <CardContent>
                        <Typography variant="h5" component="h1" gutterBottom>
                            Check Your Email
                        </Typography>
                        <Alert severity="success" sx={{ mb: 2 }}>
                            If an account exists with that email address, you will receive a password reset link shortly.
                        </Alert>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            The link will expire in 1 hour. If you don't see the email, check your spam folder.
                        </Typography>
                        <Button variant="contained" onClick={() => navigate({ to: '/' })}>
                            Back to Login
                        </Button>
                    </CardContent>
                </Card>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Card>
                <CardContent>
                    <Typography variant="h5" component="h1" gutterBottom>
                        Forgot Password
                    </Typography>
                    <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                        Enter your email address and we'll send you a link to reset your password.
                    </Typography>

                    <Box component="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={2}>
                        <TextField
                            label="Email"
                            type="text"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            required
                            disabled={isLoading}
                            fullWidth
                            autoFocus
                        />
                        {error && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                                {error}
                            </Alert>
                        )}
                        <Button type="submit" variant="contained" disabled={isLoading} sx={{ mt: 2 }}>
                            {isLoading ? 'Sending...' : 'Send Reset Link'}
                        </Button>
                    </Box>

                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Link
                            component="button"
                            type="button"
                            variant="body2"
                            onClick={() => navigate({ to: '/' })}
                            sx={{ cursor: 'pointer' }}
                        >
                            Back to Login
                        </Link>
                    </Box>
                </CardContent>
            </Card>
        </Container>
    );
};

export default ForgotPassword;
